import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SubscriptionActionsCard = ({ currentTier, onPause, onCancel, onReactivate }) => {
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePause = () => {
    setIsProcessing(true);
    setTimeout(() => {
      onPause();
      setIsProcessing(false);
      setShowPauseDialog(false);
    }, 1500);
  };

  const handleCancel = () => {
    setIsProcessing(true);
    setTimeout(() => {
      onCancel();
      setIsProcessing(false);
      setShowCancelDialog(false);
    }, 1500);
  };

  return (
    <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden">
      <div className="p-6 border-b border-border">
        <h3 className="text-lg font-heading font-semibold text-foreground">Subscription Actions</h3>
        <p className="text-sm text-muted-foreground mt-1">Manage your subscription settings</p>
      </div>

      <div className="p-6 space-y-4">
        <div className="p-4 bg-muted/30 rounded-lg">
          <div className="flex items-start space-x-3 mb-3">
            <Icon name="Pause" size={20} className="text-warning flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-foreground mb-1">Pause Subscription</h4>
              <p className="text-xs text-muted-foreground">
                Take a break for up to 3 months. Your data and projects will be saved.
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            iconName="Pause"
            iconPosition="left"
            onClick={() => setShowPauseDialog(true)}
          >
            Pause Subscription
          </Button>
        </div>

        <div className="p-4 bg-error/5 border border-error/20 rounded-lg">
          <div className="flex items-start space-x-3 mb-3">
            <Icon name="XCircle" size={20} className="text-error flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h4 className="text-sm font-medium text-foreground mb-1">Cancel Subscription</h4>
              <p className="text-xs text-muted-foreground">
                Cancel anytime. You'll retain access until the end of your billing period.
              </p>
            </div>
          </div>
          <Button
            variant="destructive"
            size="sm"
            iconName="XCircle"
            iconPosition="left"
            onClick={() => setShowCancelDialog(true)}
          >
            Cancel Subscription
          </Button>
        </div>

        <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
          <div className="flex items-start space-x-3">
            <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Need Help?</p>
              <p className="text-xs text-muted-foreground mb-2">
                Our support team is here to help with any subscription questions or concerns.
              </p>
              <Button variant="ghost" size="xs" iconName="Mail" iconPosition="left">
                Contact Support
              </Button>
            </div>
          </div>
        </div>
      </div>

      {showPauseDialog && (
        <>
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50" onClick={() => !isProcessing && setShowPauseDialog(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50 p-4">
            <div className="bg-card border border-border rounded-lg shadow-2xl p-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-10 h-10 bg-warning/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Pause" size={20} className="text-warning" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-1">Pause Subscription?</h3>
                  <p className="text-sm text-muted-foreground">
                    Your subscription will be paused and you can reactivate anytime within 3 months.
                  </p>
                </div>
              </div>

              <div className="space-y-2 mb-6 p-4 bg-muted/30 rounded-lg">
                <p className="text-xs font-medium text-foreground">What happens when you pause:</p>
                <ul className="text-xs text-muted-foreground space-y-1">
                  <li>• Billing stops immediately</li>
                  <li>• Projects and data remain saved</li>
                  <li>• Community access paused</li>
                  <li>• Reactivate anytime within 90 days</li>
                </ul>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="default"
                  fullWidth
                  onClick={() => setShowPauseDialog(false)}
                  disabled={isProcessing}
                >
                  Keep Active
                </Button>
                <Button
                  variant="default"
                  size="default"
                  fullWidth
                  loading={isProcessing}
                  onClick={handlePause}
                >
                  Pause Subscription
                </Button>
              </div>
            </div>
          </div>
        </>
      )}

      {showCancelDialog && (
        <>
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50" onClick={() => !isProcessing && setShowCancelDialog(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-md z-50 p-4">
            <div className="bg-card border border-border rounded-lg shadow-2xl p-6">
              <div className="flex items-start space-x-3 mb-4">
                <div className="w-10 h-10 bg-error/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="AlertTriangle" size={20} className="text-error" />
                </div>
                <div>
                  <h3 className="text-lg font-heading font-semibold text-foreground mb-1">Cancel Subscription?</h3>
                  <p className="text-sm text-muted-foreground">
                    We're sorry to see you go. Your access will continue until 01/04/2026.
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-6">
                <div className="p-4 bg-warning/5 border border-warning/20 rounded-lg">
                  <p className="text-xs font-medium text-foreground mb-2">Before you cancel:</p>
                  <ul className="text-xs text-muted-foreground space-y-1">
                    <li>• You'll lose access to all AI agents</li>
                    <li>• Community access will be removed</li>
                    <li>• Projects will be archived after 30 days</li>
                    <li>• You can resubscribe anytime</li>
                  </ul>
                </div>

                <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <p className="text-xs font-medium text-foreground mb-1">Special Offer</p>
                  <p className="text-xs text-muted-foreground">
                    Get 20% off your next 3 months if you stay subscribed
                  </p>
                </div>
              </div>

              <div className="flex items-center space-x-3">
                <Button
                  variant="outline"
                  size="default"
                  fullWidth
                  onClick={() => setShowCancelDialog(false)}
                  disabled={isProcessing}
                >
                  Keep Subscription
                </Button>
                <Button
                  variant="destructive"
                  size="default"
                  fullWidth
                  loading={isProcessing}
                  onClick={handleCancel}
                >
                  Cancel Anyway
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SubscriptionActionsCard;